import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    deleteProductByDocumentId,
    getProducts,
    getSubcategories, getSubcategoriesList
} from '../helper/CMSApi';
import { toast, ToastContainer } from "react-toastify";

const ProductList = () => {
    const [searchType, setSearchType] = useState<'name' | 'productNumber' | ''>('');
    const [searchValue, setSearchValue] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState<string>(''); // subcategory documentId
    const [subcategories, setSubcategories] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const pageSize = 50;
    const navigate = useNavigate();

    /**
     * Load subcategories once the component mounts.
     */
    useEffect(() => {
        loadSubcategories();
    }, []);

    const loadSubcategories = async () => {
        try {
            const response = await getSubcategoriesList();
            if (response && response.data) {
                setSubcategories(response.data);
            }
        } catch (error) {
            console.error('Error fetching subcategories:', error);
        }
    };

    // Fetch products whenever `page` changes or after the user triggers a search.
    useEffect(() => {
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const filters: {
                productNumber?: string;
                name?: string;
                subcategoryDocId?: string;
                page: number;
                pageSize: number;
            } = {
                page,
                pageSize,
            };
            if (searchType === 'productNumber' && searchValue.trim()) {
                filters.productNumber = searchValue;
            }
            else if (searchType === 'name' && searchValue.trim()) {
                filters.name = searchValue;
            }
            if (selectedSubcategory) {
                filters.subcategoryDocId = selectedSubcategory;
            }

            const response = await getProducts(filters);
            if (response && response.data) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        }
        setLoading(false);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1); // Reset to page 1 on new search
        fetchProducts();
    };

    const handleEdit = (productId: number | string) => {
        navigate(`/edit-product/${productId}`);
    };

    const handleDelete = async (documentId: string) => {
        try {
            await deleteProductByDocumentId(documentId);
            setProducts((prevProducts) => prevProducts.filter(
                (product) => product.documentId !== documentId
            ));
            toast.success('Product deleted successfully');
        } catch (e) {
            toast.error(`Something went wrong while deleting product: ${e}`);
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-6 bg-gray-800 text-white shadow rounded">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header section with "Create product" button */}
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Product List</h1>
                <button
                    onClick={() => navigate('/create-product')}
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded"
                >
                    Create Product
                </button>
            </div>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="space-y-4">
                <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
                    {/* Search Type */}
                    <div className="flex flex-col">
                        <label className="block text-gray-300 font-medium">Search Type:</label>
                        <select
                            value={searchType}
                            onChange={(e) =>
                                setSearchType(e.target.value as 'name' | 'productNumber' | '')
                            }
                            className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                        >
                            <option value="">-- Select --</option>
                            <option value="name">Name</option>
                            <option value="productNumber">Product Number</option>
                        </select>
                    </div>

                    {/* Search Value */}
                    <div className="flex flex-col w-full">
                        <label className="block text-gray-300 font-medium">Search Value:</label>
                        <input
                            type="text"
                            placeholder="Enter search value"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                        />
                    </div>
                </div>

                {/* Subcategory Selection */}
                <div className="flex flex-col w-full">
                    <label className="block text-gray-300 font-medium">Subcategory:</label>
                    <select
                        value={selectedSubcategory}
                        onChange={(e) => setSelectedSubcategory(e.target.value)}
                        className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                    >
                        <option value="">All subcategories</option>
                        {subcategories.map((subcategory) => (
                            <option key={subcategory.id} value={subcategory.documentId}>
                                {subcategory.localizedName}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                >
                    Search
                </button>
            </form>

            {loading ? (
                <p className="text-center py-4">Loading products...</p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-600 mt-4">
                            <thead className="bg-gray-700">
                            <tr>
                                <th className="p-3 border-b border-gray-600 text-left">Image</th>
                                <th className="p-3 border-b border-gray-600 text-left">Name</th>
                                <th className="p-3 border-b border-gray-600 text-left">Product Number</th>
                                <th className="p-3 border-b border-gray-600 text-left">Rating</th>
                                <th className="p-3 border-b border-gray-600 text-left">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {products.map((item: any) => (
                                <tr key={item.id} className="hover:bg-gray-750">
                                    <td className="p-3 border-b border-gray-600">
                                        {item.image?.formats?.thumbnail?.url ? (
                                            <img
                                                src={`${process.env.REACT_APP_STRAPI_URL}${item.image.formats.thumbnail.url}`}
                                                alt={item.name || 'Product Image'}
                                                className="w-16 h-16 object-cover"
                                            />
                                        ) : (
                                            <span>No Image</span>
                                        )}
                                    </td>
                                    <td className="p-3 border-b border-gray-600">
                                        {item.name || 'N/A'}
                                    </td>
                                    <td className="p-3 border-b border-gray-600">
                                        {item.productNumber || 'N/A'}
                                    </td>
                                    <td className="p-3 border-b border-gray-600">
                                        {item.rating || 'N/A'}
                                    </td>
                                    <td className="p-3 border-b border-gray-600 space-x-2">
                                        <button
                                            onClick={() => handleEdit(item.documentId)}
                                            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-1 px-3 rounded"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.documentId)}
                                            className="bg-red-500 hover:bg-red-600 text-white font-bold py-1 px-3 rounded"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-center space-x-4 mt-4">
                        <button
                            disabled={page === 1}
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                            className={`${
                                page === 1
                                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                            } font-bold py-2 px-4 rounded`}
                        >
                            Previous
                        </button>
                        <span>Page {page}</span>
                        <button
                            onClick={() => setPage((prev) => prev + 1)}
                            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                        >
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ProductList;
